import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type { AiModelProfileDto, CopilotPromptConfigurationsDto } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { SectionCard } from "@/design-system/SectionCard";
import { Select } from "@/design-system/Select";
import { TextArea } from "@/design-system/TextArea";
import { Tiles } from "@/design-system/Tiles";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import {
  copilotProfileOptions,
  getCopilotProfileName,
  getCopilotPromptTypeName,
} from "@/modules/project/pages/integrations/pages/copilotProfiles";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

import { BulkOperationResults, BulkOperationReview, type BulkOperationReviewField } from "./BulkOperationReview";
import { CONFIGURATION_REQUEST_CONCURRENCY, getErrorMessage, mapWithConcurrency, prepareAiProject } from "./helpers";
import type { BulkOperationResult, PortfolioIntegrationProject } from "./types";

type AiBulkField = "communityPrompt" | "ticketPrompt" | "profile";

type BulkAiEditorProps = {
  rows: PortfolioIntegrationProject<CopilotPromptConfigurationsDto>[];
  selectedProjectIds: Set<string>;
  onContinueToProjectSetup: () => void;
};

export function BulkAiEditor({
  rows,
  selectedProjectIds,
  onContinueToProjectSetup,
}: BulkAiEditorProps): React.ReactNode {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const sessionUser = useSessionUser();
  const selectedRows = useMemo(
    () => rows.filter((row) => row.canManage && selectedProjectIds.has(row.project.id)),
    [rows, selectedProjectIds],
  );
  const availableProfiles = copilotProfileOptions.filter(
    (option) => sessionUser.isSuperAdmin || !option.restrictedToSuperAdmins,
  );

  const [stage, setStage] = useState<"edit" | "review" | "results">("edit");
  const [isApplying, setIsApplying] = useState(false);
  const [results, setResults] = useState<BulkOperationResult[]>([]);
  const [changedFields, setChangedFields] = useState<Record<AiBulkField, boolean>>({
    communityPrompt: false,
    ticketPrompt: false,
    profile: false,
  });
  const [communityPrompt, setCommunityPrompt] = useState("");
  const [ticketPrompt, setTicketPrompt] = useState("");
  const [profile, setProfile] = useState<AiModelProfileDto>("efficient");

  const hasBlankPrompt =
    (changedFields.communityPrompt && !communityPrompt.trim()) || (changedFields.ticketPrompt && !ticketPrompt.trim());

  const preparedProjects = selectedRows.map((row) =>
    prepareAiProject(
      row,
      {
        communityPrompt: changedFields.communityPrompt ? communityPrompt : undefined,
        ticketPrompt: changedFields.ticketPrompt ? ticketPrompt : undefined,
        profile: changedFields.profile ? profile : undefined,
      },
      { notConfigured: t("page.portfolio-integrations.preflight.ai-not-configured") },
    ),
  );
  const validProjects = preparedProjects.filter((project) => !project.reason);
  const skippedProjects = preparedProjects.filter(
    (project): project is (typeof preparedProjects)[number] & { reason: string } => Boolean(project.reason),
  );
  const fields = useMemo(() => {
    const reviewFields: BulkOperationReviewField[] = [];
    if (changedFields.communityPrompt) {
      reviewFields.push({ label: getCopilotPromptTypeName(t, "community"), value: communityPrompt.trim() });
    }
    if (changedFields.ticketPrompt) {
      reviewFields.push({ label: getCopilotPromptTypeName(t, "ticket"), value: ticketPrompt.trim() });
    }
    if (changedFields.profile) {
      reviewFields.push({
        label: t("page.integration.copilot-configurations.profile"),
        value: getCopilotProfileName(t, profile),
      });
    }

    return reviewFields;
  }, [changedFields, communityPrompt, profile, t, ticketPrompt]);

  const apply = async (projectIds?: string[]) => {
    setIsApplying(true);
    try {
      const targets = validProjects.filter((project) => !projectIds || projectIds.includes(project.id));
      const settled = await mapWithConcurrency(targets, CONFIGURATION_REQUEST_CONCURRENCY, async (project) => {
        try {
          await api.putIntegrationsCopilotPromptConfigurationsV1(project.payload, {
            headers: { [ProjectHeaderName]: project.id },
          });

          return { status: "fulfilled", value: project } as const;
        } catch (reason) {
          return { status: "rejected", reason } as const;
        }
      });
      const updatedResults = settled.map((result, index): BulkOperationResult => {
        const project = targets[index];

        return result.status === "fulfilled"
          ? { projectId: project.id, projectName: project.name, status: "updated" }
          : {
              projectId: project.id,
              projectName: project.name,
              status: "failed",
              reason: getErrorMessage(result.reason, t("page.portfolio-integrations.results.update-failed")),
            };
      });

      await Promise.all([
        ...updatedResults
          .filter((result) => result.status === "updated")
          .map((result) =>
            queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.INTEGRATION_COPILOT_CONFIGURATION(result.projectId),
            }),
          ),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_INTEGRATION_CONFIGURATIONS("ai") }),
      ]);

      if (projectIds) {
        setResults((current) => [
          ...current.filter((result) => !projectIds.includes(result.projectId)),
          ...updatedResults,
        ]);
      } else {
        setResults([
          ...updatedResults,
          ...skippedProjects.map((project) => ({
            projectId: project.id,
            projectName: project.name,
            status: "skipped" as const,
            reason: project.reason,
          })),
        ]);
      }
      setStage("results");
    } finally {
      setIsApplying(false);
    }
  };

  if (stage === "results") {
    return (
      <BulkOperationResults
        results={results}
        isRetrying={isApplying}
        onRetry={(projectIds) => void apply(projectIds)}
        onContinue={onContinueToProjectSetup}
      />
    );
  }

  return (
    <>
      <SectionCard
        title={t("page.portfolio-integrations.ai.bulk-editable-title")}
        description={t("page.portfolio-integrations.ai.bulk-editable-description")}
      >
        <Tiles columns={{ base: 1, container: { lg: 2 } }} gap="md">
          <BulkAiPromptField
            field="communityPrompt"
            label={getCopilotPromptTypeName(t, "community")}
            value={communityPrompt}
            changed={changedFields.communityPrompt}
            onChanged={(changed) => setChangedFields((current) => ({ ...current, communityPrompt: changed }))}
            onValue={setCommunityPrompt}
          />
          <BulkAiPromptField
            field="ticketPrompt"
            label={getCopilotPromptTypeName(t, "ticket")}
            value={ticketPrompt}
            changed={changedFields.ticketPrompt}
            onChanged={(changed) => setChangedFields((current) => ({ ...current, ticketPrompt: changed }))}
            onValue={setTicketPrompt}
          />
          <FormField
            label={t("page.integration.copilot-configurations.profile")}
            description={
              changedFields.profile
                ? t("page.portfolio-integrations.ai.profile-will-change")
                : t("page.portfolio-integrations.field.keep-current")
            }
            actions={
              <Checkbox
                name="change-profile"
                checked={changedFields.profile}
                label={t("page.portfolio-integrations.field.change")}
                onChange={(event) => setChangedFields((current) => ({ ...current, profile: event.target.checked }))}
              />
            }
          >
            <Select
              width="standard"
              items={availableProfiles}
              selected={availableProfiles.find((option) => option.value === profile)}
              keySelector={(option) => option.value}
              renderOption={(option) => getCopilotProfileName(t, option.value)}
              disabled={!changedFields.profile}
              onChange={(option) => setProfile(option.value)}
            />
          </FormField>
        </Tiles>

        {hasBlankPrompt && (
          <Notice variant="warning" message={t("page.portfolio-integrations.preflight.ai-empty-prompt")} />
        )}

        <Form.Footer
          renderPrimaryAction={() => (
            <Button
              disabled={selectedRows.length === 0 || fields.length === 0 || hasBlankPrompt}
              onClick={() => setStage("review")}
            >
              {t("page.portfolio-integrations.action.review")}
            </Button>
          )}
        />
      </SectionCard>
      {stage === "review" && (
        <BulkOperationReview
          title={t("page.portfolio-integrations.review.title")}
          description={t("page.portfolio-integrations.ai.review-description")}
          fields={fields}
          validProjects={validProjects}
          skippedProjects={skippedProjects}
          isApplying={isApplying}
          onBack={() => setStage("edit")}
          onApply={() => void apply()}
        />
      )}
    </>
  );
}

function BulkAiPromptField({
  field,
  label,
  value,
  changed,
  onChanged,
  onValue,
}: {
  field: "communityPrompt" | "ticketPrompt";
  label: string;
  value: string;
  changed: boolean;
  onChanged: (changed: boolean) => void;
  onValue: (value: string) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <FormField
      label={label}
      description={
        changed
          ? t("page.portfolio-integrations.field.will-change")
          : t("page.portfolio-integrations.field.keep-current")
      }
      actions={
        <Checkbox
          name={`change-${field}`}
          checked={changed}
          label={t("page.portfolio-integrations.field.change")}
          onChange={(event) => onChanged(event.target.checked)}
        />
      }
    >
      <TextArea
        value={value}
        disabled={!changed}
        height="sm"
        placeholder={t("page.integration.copilot-configurations.prompt.placeholder")}
        onChange={(event) => onValue(event.target.value)}
      />
    </FormField>
  );
}
